import { z } from "zod"

const APPLICATION_ERROR_MARKER = "aix.application-error.v2"
const MAX_CAUSE_DEPTH = 8

export enum EApplicationErrorCode {
  AUTH_EXPIRED = "auth_expired",
  CONCURRENCY_LIMITED = "concurrency_limited",
  INSUFFICIENT_CREDITS = "insufficient_credits",
  INTERNAL_INVARIANT_FAILED = "internal_invariant_failed",
  PROVIDER_CONTRACT_VIOLATION = "provider_contract_violation",
  PROVIDER_UNAVAILABLE = "provider_unavailable",
  REQUIRED_RESOURCE_MISSING = "required_resource_missing",
  USER_INPUT_REJECTED = "user_input_rejected",
  USER_RESOURCE_NOT_FOUND = "user_resource_not_found",
}

export enum EApplicationErrorPublicCode {
  AUTHENTICATION_REQUIRED = "authentication_required",
  CAPACITY_LIMITED = "capacity_limited",
  INSUFFICIENT_CREDITS = "insufficient_credits",
  INTERNAL_ERROR = "internal_error",
  INVALID_INPUT = "invalid_input",
  NOT_FOUND = "not_found",
  SERVICE_UNAVAILABLE = "service_unavailable",
}

export enum EApplicationErrorClassification {
  EXPECTED = "expected",
  UNEXPECTED = "unexpected",
}

export enum EApplicationErrorRecoveryAction {
  NONE = "none",
  REAUTHENTICATE = "reauthenticate",
  RETRY_BACKOFF = "retry_backoff",
  REVISE_INPUT = "revise_input",
  TOP_UP_CREDITS = "top_up_credits",
  WAIT_FOR_CAPACITY = "wait_for_capacity",
}

export enum EApplicationErrorReporting {
  ERROR_TRACKING = "error_tracking",
  OPERATIONAL_ONLY = "operational_only",
}

interface IApplicationErrorPolicy {
  classification: EApplicationErrorClassification
  publicCode: EApplicationErrorPublicCode
  recoveryAction: EApplicationErrorRecoveryAction
  reporting: EApplicationErrorReporting
  clientMessage?: string
}

const APPLICATION_ERROR_POLICIES: Record<EApplicationErrorCode, IApplicationErrorPolicy> = {
  [EApplicationErrorCode.AUTH_EXPIRED]: {
    classification: EApplicationErrorClassification.EXPECTED,
    publicCode: EApplicationErrorPublicCode.AUTHENTICATION_REQUIRED,
    recoveryAction: EApplicationErrorRecoveryAction.REAUTHENTICATE,
    reporting: EApplicationErrorReporting.OPERATIONAL_ONLY,
  },
  [EApplicationErrorCode.CONCURRENCY_LIMITED]: {
    classification: EApplicationErrorClassification.EXPECTED,
    publicCode: EApplicationErrorPublicCode.CAPACITY_LIMITED,
    recoveryAction: EApplicationErrorRecoveryAction.WAIT_FOR_CAPACITY,
    reporting: EApplicationErrorReporting.OPERATIONAL_ONLY,
  },
  [EApplicationErrorCode.INSUFFICIENT_CREDITS]: {
    classification: EApplicationErrorClassification.EXPECTED,
    publicCode: EApplicationErrorPublicCode.INSUFFICIENT_CREDITS,
    recoveryAction: EApplicationErrorRecoveryAction.TOP_UP_CREDITS,
    reporting: EApplicationErrorReporting.OPERATIONAL_ONLY,
  },
  [EApplicationErrorCode.INTERNAL_INVARIANT_FAILED]: {
    classification: EApplicationErrorClassification.UNEXPECTED,
    publicCode: EApplicationErrorPublicCode.INTERNAL_ERROR,
    recoveryAction: EApplicationErrorRecoveryAction.NONE,
    reporting: EApplicationErrorReporting.ERROR_TRACKING,
    clientMessage: "An unexpected error occurred",
  },
  [EApplicationErrorCode.PROVIDER_CONTRACT_VIOLATION]: {
    classification: EApplicationErrorClassification.UNEXPECTED,
    publicCode: EApplicationErrorPublicCode.INTERNAL_ERROR,
    recoveryAction: EApplicationErrorRecoveryAction.NONE,
    reporting: EApplicationErrorReporting.ERROR_TRACKING,
    clientMessage: "An unexpected error occurred",
  },
  [EApplicationErrorCode.PROVIDER_UNAVAILABLE]: {
    classification: EApplicationErrorClassification.UNEXPECTED,
    publicCode: EApplicationErrorPublicCode.SERVICE_UNAVAILABLE,
    recoveryAction: EApplicationErrorRecoveryAction.RETRY_BACKOFF,
    reporting: EApplicationErrorReporting.OPERATIONAL_ONLY,
    clientMessage: "The service is temporarily unavailable",
  },
  [EApplicationErrorCode.REQUIRED_RESOURCE_MISSING]: {
    classification: EApplicationErrorClassification.UNEXPECTED,
    publicCode: EApplicationErrorPublicCode.INTERNAL_ERROR,
    recoveryAction: EApplicationErrorRecoveryAction.NONE,
    reporting: EApplicationErrorReporting.ERROR_TRACKING,
    clientMessage: "An unexpected error occurred",
  },
  [EApplicationErrorCode.USER_INPUT_REJECTED]: {
    classification: EApplicationErrorClassification.EXPECTED,
    publicCode: EApplicationErrorPublicCode.INVALID_INPUT,
    recoveryAction: EApplicationErrorRecoveryAction.REVISE_INPUT,
    reporting: EApplicationErrorReporting.OPERATIONAL_ONLY,
  },
  [EApplicationErrorCode.USER_RESOURCE_NOT_FOUND]: {
    classification: EApplicationErrorClassification.EXPECTED,
    publicCode: EApplicationErrorPublicCode.NOT_FOUND,
    recoveryAction: EApplicationErrorRecoveryAction.NONE,
    reporting: EApplicationErrorReporting.OPERATIONAL_ONLY,
  },
}

const boundedLabel = z.string().min(1).max(128)
const boundedIdentifier = z.string().min(1).max(256)
const nonNegativeFiniteNumber = z.number().finite().nonnegative()

const APPLICATION_ERROR_METADATA_SCHEMAS = {
  [EApplicationErrorCode.AUTH_EXPIRED]: z
    .object({
      authMethod: boundedLabel.optional(),
    })
    .strict(),
  [EApplicationErrorCode.CONCURRENCY_LIMITED]: z
    .object({
      resource: boundedLabel,
      retryAfterMs: nonNegativeFiniteNumber.optional(),
    })
    .strict(),
  [EApplicationErrorCode.INSUFFICIENT_CREDITS]: z
    .object({
      teamId: boundedIdentifier,
      availableCredits: nonNegativeFiniteNumber,
      requestedCredits: nonNegativeFiniteNumber,
    })
    .strict(),
  [EApplicationErrorCode.INTERNAL_INVARIANT_FAILED]: z
    .object({
      operation: boundedLabel,
    })
    .strict(),
  [EApplicationErrorCode.PROVIDER_CONTRACT_VIOLATION]: z
    .object({
      operation: boundedLabel,
      provider: boundedLabel,
    })
    .strict(),
  [EApplicationErrorCode.PROVIDER_UNAVAILABLE]: z
    .object({
      operation: boundedLabel,
      provider: boundedLabel,
      statusCode: z.number().int().min(100).max(599).optional(),
    })
    .strict(),
  [EApplicationErrorCode.REQUIRED_RESOURCE_MISSING]: z
    .object({
      operation: boundedLabel,
      resourceType: boundedLabel,
    })
    .strict(),
  [EApplicationErrorCode.USER_INPUT_REJECTED]: z
    .object({
      field: boundedLabel.optional(),
      provider: boundedLabel.optional(),
    })
    .strict(),
  [EApplicationErrorCode.USER_RESOURCE_NOT_FOUND]: z
    .object({
      resourceId: boundedIdentifier.optional(),
      resourceType: boundedLabel,
    })
    .strict(),
} satisfies Record<EApplicationErrorCode, z.ZodType<Record<string, unknown>>>

export type TApplicationErrorMetadata<TCode extends EApplicationErrorCode> = z.infer<
  (typeof APPLICATION_ERROR_METADATA_SCHEMAS)[TCode]
>

export interface IApplicationErrorDetails {
  marker: typeof APPLICATION_ERROR_MARKER
  code: EApplicationErrorCode
  publicCode: EApplicationErrorPublicCode
  classification: EApplicationErrorClassification
  recoveryAction: EApplicationErrorRecoveryAction
  reporting: EApplicationErrorReporting
  message: string
  metadata: TApplicationErrorMetadata<EApplicationErrorCode>
}

export type TApplicationError = Error & {
  applicationError: IApplicationErrorDetails
}

export interface ICreateApplicationErrorInput<TCode extends EApplicationErrorCode> {
  code: TCode
  message: string
  metadata: TApplicationErrorMetadata<TCode>
  cause?: unknown
}

interface IInsufficientCreditsErrorInput {
  teamId: string
  availableCredits: number
  requestedCredits: number
}

type TApplicationErrorObservationValue = string | number | boolean | undefined

export interface IApplicationErrorObservation {
  boundary: string
  code: EApplicationErrorCode
  publicCode: EApplicationErrorPublicCode
  classification: EApplicationErrorClassification
  recoveryAction: EApplicationErrorRecoveryAction
  reporting: EApplicationErrorReporting
  metadata: TApplicationErrorMetadata<EApplicationErrorCode>
  context: Record<string, TApplicationErrorObservationValue>
}

interface IObserveApplicationErrorOptions {
  boundary: string
  context?: Record<string, TApplicationErrorObservationValue>
  emit: (observation: IApplicationErrorObservation) => void
  report: (error: unknown, observation: IApplicationErrorObservation) => void
}

interface IApplicationErrorMatch {
  details: IApplicationErrorDetails
  identity: object
}

const observedApplicationErrors = new WeakSet<object>()
const reportedApplicationErrors = new WeakSet<object>()
const RETRYABLE_RECOVERY_ACTIONS = new Set<EApplicationErrorRecoveryAction>([
  EApplicationErrorRecoveryAction.REAUTHENTICATE,
  EApplicationErrorRecoveryAction.RETRY_BACKOFF,
  EApplicationErrorRecoveryAction.WAIT_FOR_CAPACITY,
])

const asRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined
  return value as Record<string, unknown>
}

const isApplicationErrorCode = (value: unknown): value is EApplicationErrorCode =>
  typeof value === "string" && Object.values(EApplicationErrorCode).includes(value as EApplicationErrorCode)

const parseApplicationErrorDetails = (value: unknown): IApplicationErrorDetails | undefined => {
  const details = asRecord(value)
  if (details?.marker !== APPLICATION_ERROR_MARKER || !isApplicationErrorCode(details.code)) return undefined

  const policy = APPLICATION_ERROR_POLICIES[details.code]
  if (
    details.classification !== policy.classification ||
    details.publicCode !== policy.publicCode ||
    details.recoveryAction !== policy.recoveryAction ||
    details.reporting !== policy.reporting ||
    typeof details.message !== "string"
  ) {
    return undefined
  }

  const parsedMetadata = APPLICATION_ERROR_METADATA_SCHEMAS[details.code].safeParse(details.metadata)
  if (!parsedMetadata.success) return undefined

  return {
    marker: APPLICATION_ERROR_MARKER,
    code: details.code,
    publicCode: policy.publicCode,
    classification: policy.classification,
    recoveryAction: policy.recoveryAction,
    reporting: policy.reporting,
    message: details.message,
    metadata: parsedMetadata.data,
  }
}

const findApplicationError = (error: unknown): IApplicationErrorMatch | undefined => {
  let current: unknown = error
  const visited = new Set<object>()

  for (let depth = 0; depth < MAX_CAUSE_DEPTH; depth += 1) {
    const record = asRecord(current)
    if (!record || visited.has(record)) return undefined
    visited.add(record)

    const applicationErrorRecord = asRecord(record.applicationError)
    const details = parseApplicationErrorDetails(applicationErrorRecord)
    if (details && applicationErrorRecord) {
      return { details, identity: applicationErrorRecord }
    }
    current = record.cause
  }

  return undefined
}

export const createApplicationError = <TCode extends EApplicationErrorCode>(
  input: ICreateApplicationErrorInput<TCode>,
): TApplicationError => {
  const policy = APPLICATION_ERROR_POLICIES[input.code]
  const metadata = APPLICATION_ERROR_METADATA_SCHEMAS[input.code].parse(input.metadata)
  const error = Object.assign(new Error(input.message), input.cause === undefined ? {} : { cause: input.cause })
  const applicationError: IApplicationErrorDetails = {
    marker: APPLICATION_ERROR_MARKER,
    code: input.code,
    publicCode: policy.publicCode,
    classification: policy.classification,
    recoveryAction: policy.recoveryAction,
    reporting: policy.reporting,
    message: input.message,
    metadata,
  }

  return Object.assign(error, { applicationError })
}

export const createInsufficientCreditsError = (input: IInsufficientCreditsErrorInput): TApplicationError => {
  const unit = input.requestedCredits === 1 ? "credit" : "credits"
  return createApplicationError({
    code: EApplicationErrorCode.INSUFFICIENT_CREDITS,
    message: `Insufficient credits. Required: ${input.requestedCredits} ${unit}`,
    metadata: {
      teamId: input.teamId,
      availableCredits: input.availableCredits,
      requestedCredits: input.requestedCredits,
    },
  })
}

export const getApplicationError = (error: unknown): IApplicationErrorDetails | undefined =>
  findApplicationError(error)?.details

export const getApplicationErrorClientMessage = (error: unknown): string | undefined => {
  const applicationError = getApplicationError(error)
  if (!applicationError) return undefined
  return APPLICATION_ERROR_POLICIES[applicationError.code].clientMessage ?? applicationError.message
}

export const isExpectedApplicationError = (error: unknown): boolean =>
  getApplicationError(error)?.classification === EApplicationErrorClassification.EXPECTED

export const isApplicationErrorRetryable = (error: unknown): boolean => {
  const recoveryAction = getApplicationError(error)?.recoveryAction
  return recoveryAction !== undefined && RETRYABLE_RECOVERY_ACTIONS.has(recoveryAction)
}

export const shouldReportApplicationErrorToErrorTracking = (error: unknown): boolean =>
  getApplicationError(error)?.reporting === EApplicationErrorReporting.ERROR_TRACKING

export const claimApplicationErrorForErrorTracking = (error: unknown): boolean => {
  const match = findApplicationError(error)
  if (!match || match.details.reporting !== EApplicationErrorReporting.ERROR_TRACKING) return false
  if (reportedApplicationErrors.has(match.identity)) return false

  reportedApplicationErrors.add(match.identity)
  return true
}

export const observeApplicationError = (error: unknown, options: IObserveApplicationErrorOptions): boolean => {
  const match = findApplicationError(error)
  if (!match) return false
  if (observedApplicationErrors.has(match.identity)) return true

  observedApplicationErrors.add(match.identity)
  const observation: IApplicationErrorObservation = {
    boundary: options.boundary,
    code: match.details.code,
    publicCode: match.details.publicCode,
    classification: match.details.classification,
    recoveryAction: match.details.recoveryAction,
    reporting: match.details.reporting,
    metadata: match.details.metadata,
    context: options.context ?? {},
  }

  options.emit(observation)
  if (match.details.reporting === EApplicationErrorReporting.ERROR_TRACKING) {
    options.report(error, observation)
  }
  return true
}

export const rethrowApplicationError = (error: unknown): void => {
  if (getApplicationError(error)) throw error
}
